import { Component, type ErrorInfo, type ReactNode } from 'react';
import { RenderErrorPage } from './RenderErrorPage';

interface Props {
  children: ReactNode;
  /**
   * Optional scoped fallback. When provided, a caught error renders this
   * instead of the full-page display error — used to isolate a single
   * widget/section so one failure doesn't blank the whole page.
   */
  fallback?: ReactNode;
}

interface State {
  error: Error | null;
}

// Catches uncaught render-phase errors anywhere in the React tree and shows a
// display-error page instead of a white screen. These are client-side bugs, so
// they deliberately do NOT render the 500/server page — mislabelling them sends
// debugging effort to backend logs that hold no trace of the fault. Async errors
// (promise rejections) aren't caught here either; those flow through the axios
// interceptor and TanStack Query's `error` state, handled inline per component.
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Best-effort console trace for dev; replace with a logging integration
    // (Sentry / Datadog) if/when one is wired up.
     
    console.error('Uncaught render error:', error, info);
  }

  private reset = () => {
    this.setState({ error: null });
  };

  override render() {
    if (this.state.error) {
      if (this.props.fallback !== undefined) return this.props.fallback;
      const message = this.state.error.message || 'Unknown error';
      return (
        <RenderErrorPage
          reason={message}
          onRetry={() => {
            this.reset();
            window.location.reload();
          }}
        />
      );
    }
    return this.props.children;
  }
}
